import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { getCategoryByKey } from "../../../lib/training";
import UserTopNav from "../../../components/layout/UserTopNav";
import { gymConfig } from "../../../config/gymConfig";
import {
  formatDisplayValue,
  isBetterPerformance,
  parseChartValue,
  prettyDate,
} from "../utils/movementHelpers";

type CategoryTrainingLog = {
  id: string;
  movementSlug: string;
  movementName: string;
  metricType: string;
  value: string;
  unit: string;
  date: string;
};

type MovementSummary = {
  totalLogs: number;
  latestLog: CategoryTrainingLog | null;
  bestLog: CategoryTrainingLog | null;
};

function daysSince(dateString?: string | null) {
  if (!dateString) return Number.POSITIVE_INFINITY;

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) return Number.POSITIVE_INFINITY;

  const today = new Date();
  parsed.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diff = today.getTime() - parsed.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export default function TrainingCategory() {
  const { category } = useParams<{ category: string }>();
  const selectedCategory = getCategoryByKey(category);
  const [logs, setLogs] = useState<CategoryTrainingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!selectedCategory) return;

    const user = auth.currentUser;

    if (!user) {
      setLogs([]);
      setLoading(false);
      setLoadError("Sign in to view training progress.");
      return;
    }

    setLoading(true);
    setLoadError("");

    const logsRef = collection(db, "users", user.uid, "trainingLogs");
    const q = query(
      logsRef,
      where("category", "==", selectedCategory.key),
      orderBy("date", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const nextLogs: CategoryTrainingLog[] = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            id: doc.id,
            movementSlug: data.movementSlug ?? "",
            movementName: data.movementName ?? "",
            metricType: data.metricType ?? "",
            value: data.value ?? "",
            unit: data.unit ?? "",
            date: data.date ?? "",
          };
        });

        setLogs(nextLogs);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading category training logs:", error);
        setLogs([]);
        setLoading(false);
        setLoadError(error.message || "Could not load category progress.");
      }
    );

    return () => unsubscribe();
  }, [selectedCategory]);

  const movementSummaries = useMemo(() => {
    if (!selectedCategory) return new Map<string, MovementSummary>();

    const summaryMap = new Map<string, MovementSummary>();

    for (const movement of selectedCategory.movements) {
      const movementLogs = logs.filter((log) => log.movementSlug === movement.slug);
      const latestLog = movementLogs[0] ?? null;

      let bestLog: CategoryTrainingLog | null = null;
      let bestValue: number | null = null;

      for (const log of movementLogs) {
        const parsedValue = parseChartValue(log.value, log.unit);
        if (parsedValue === null) continue;

        if (
          bestLog === null ||
          isBetterPerformance({
            categoryKey: selectedCategory.key,
            nextValue: parsedValue,
            currentBestValue: bestValue,
          })
        ) {
          bestLog = log;
          bestValue = parsedValue;
        }
      }

      summaryMap.set(movement.slug, {
        totalLogs: movementLogs.length,
        latestLog,
        bestLog,
      });
    }

    return summaryMap;
  }, [logs, selectedCategory]);

  const activeMovementCount = useMemo(() => {
    return Array.from(movementSummaries.values()).filter((summary) => summary.totalLogs > 0)
      .length;
  }, [movementSummaries]);

  const latestCategoryLog = logs[0] ?? null;
  const totalLogs = logs.length;

  const categoryActions = useMemo(() => {
    if (!selectedCategory) return [];

    const summaries = selectedCategory.movements.map((movement) => {
      const summary = movementSummaries.get(movement.slug) ?? {
        totalLogs: 0,
        latestLog: null,
        bestLog: null,
      };

      return {
        movement,
        summary,
        days: daysSince(summary.latestLog?.date ?? null),
      };
    });

    const untouched = summaries.find((item) => item.summary.totalLogs === 0);
    const stale = [...summaries]
      .filter((item) => item.summary.totalLogs > 0)
      .sort((a, b) => b.days - a.days)[0];
    const retest = [...summaries]
      .filter((item) => item.summary.totalLogs >= 2 && item.days >= 14 && item.days < 45)
      .sort((a, b) => b.summary.totalLogs - a.summary.totalLogs || b.days - a.days)[0];

    const actions: Array<{
      title: string;
      detail: string;
      to: string;
      cta: string;
    }> = [];

    if (retest) {
      actions.push({
        title: `Retest ${retest.movement.name}`,
        detail: `${retest.summary.totalLogs} logged entries and no fresh benchmark for ${retest.days} days.`,
        to: `/training/${selectedCategory.key}/${retest.movement.slug}`,
        cta: "Retest movement",
      });
    }

    if (stale && stale.days >= 21) {
      actions.push({
        title: `Reopen ${stale.movement.name}`,
        detail: `Last activity was ${stale.days} days ago. A fresh result will sharpen this category view.`,
        to: `/training/${selectedCategory.key}/${stale.movement.slug}`,
        cta: "Log now",
      });
    }

    if (untouched) {
      actions.push({
        title: `Start ${untouched.movement.name}`,
        detail: "You haven’t logged this movement yet. Add a baseline to build category coverage.",
        to: `/training/${selectedCategory.key}/${untouched.movement.slug}`,
        cta: "Set baseline",
      });
    }

    if (!actions.length && summaries.length > 0) {
      actions.push({
        title: `Keep building ${selectedCategory.label}`,
        detail: "You’ve got active data here. Add another session to keep the trendline moving.",
        to: `/training/${selectedCategory.key}/${summaries[0].movement.slug}`,
        cta: "Log session",
      });
    }

    return actions.slice(0, 3);
  }, [movementSummaries, selectedCategory]);

  if (!selectedCategory) {
    return <Navigate to="/training" replace />;
  }

  const Icon = selectedCategory.icon;

  return (
    <div className="app-page min-h-screen text-white">
      <UserTopNav />

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-5 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(14,14,16,0.98),rgba(8,8,10,0.98))] shadow-[0_22px_60px_rgba(0,0,0,0.34)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(79,124,255,0.14),transparent_24%),radial-gradient(circle_at_82%_18%,rgba(121,162,255,0.10),transparent_24%),linear-gradient(180deg,rgba(255,255,255,0.02),transparent_46%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:32px_32px] opacity-[0.04]" />

          <div className="relative p-5 sm:p-6 lg:p-7">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(220px,0.75fr)] lg:items-end">
              <div className="max-w-3xl">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-white/65 backdrop-blur-md">
                  <Icon className="h-3.5 w-3.5" />
                  {selectedCategory.label}
                </div>

                <h1 className="text-3xl font-semibold tracking-[-0.04em] text-white sm:text-4xl lg:text-[2.9rem]">
                  {selectedCategory.label}
                </h1>

                <p className="mt-3 max-w-2xl text-sm leading-6 text-white/68 sm:text-[15px]">
                  {selectedCategory.description}
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] text-white/52">
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {selectedCategory.movements.length} movements
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {gymConfig.training.benchmarkLabel}
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    Logging + progress
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-end">
                <div className="rounded-[18px] border border-white/10 bg-white/[0.03] px-4 py-3.5 backdrop-blur">
                  <div className="text-[11px] font-medium text-white/45">
                    Active movements
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-white">
                    {activeMovementCount}
                  </div>
                </div>

                <Link
                  to="/training"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-white/78 backdrop-blur-md transition hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back to Training
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-3 md:grid-cols-3">
          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">Total logs</div>
            <div className="mt-2 text-2xl font-semibold text-white">{totalLogs}</div>
            <div className="mt-2 text-sm text-white/55">
              Entries recorded in {selectedCategory.label.toLowerCase()}.
            </div>
          </div>

          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">Movement coverage</div>
            <div className="mt-2 text-2xl font-semibold text-white">
              {activeMovementCount}/{selectedCategory.movements.length}
            </div>
            <div className="mt-2 text-sm text-white/55">
              Movements you’ve logged at least once.
            </div>
          </div>

          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">Latest activity</div>
            <div className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-white">
              {latestCategoryLog ? latestCategoryLog.movementName : "—"}
            </div>
            <div className="mt-2 text-sm text-white/55">
              {latestCategoryLog
                ? `${latestCategoryLog.metricType} · ${prettyDate(latestCategoryLog.date)}`
                : "No category activity yet."}
            </div>
          </div>
        </section>

        <section className="grid gap-3 lg:grid-cols-3">
          {categoryActions.map((action) => (
            <Link
              key={action.title}
              to={action.to}
              className="group rounded-[20px] border border-white/10 bg-white/[0.025] px-4 py-4 transition hover:border-white/18 hover:bg-white/[0.04]"
            >
              <div className="text-[11px] font-medium text-white/45">Next action</div>
              <div className="mt-2 text-lg font-semibold tracking-[-0.03em] text-white">
                {action.title}
              </div>
              <div className="mt-2 text-sm leading-6 text-white/58">{action.detail}</div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white/72 transition group-hover:text-white">
                {action.cta}
                <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </section>

        {loadError ? (
          <div className="rounded-[18px] border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {loadError}
          </div>
        ) : null}

        <section className="grid gap-3">
          {selectedCategory.movements.map((movement, index) => {
            const summary = movementSummaries.get(movement.slug) ?? {
              totalLogs: 0,
              latestLog: null,
              bestLog: null,
            };

            return (
              <Link
                key={movement.slug}
                to={`/training/${selectedCategory.key}/${movement.slug}`}
                className="group relative overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(14,14,16,0.96),rgba(9,9,10,0.96))] transition duration-300 hover:border-white/18 hover:bg-[linear-gradient(180deg,rgba(18,18,22,0.98),rgba(10,10,12,0.98))]"
              >
                <div
                  className={`absolute inset-y-0 left-0 w-px bg-gradient-to-b ${selectedCategory.accent} opacity-80`}
                />
                <div className="absolute inset-0 opacity-0 transition duration-300 group-hover:opacity-100 bg-[radial-gradient(circle_at_left,rgba(255,255,255,0.05),transparent_34%)]" />

                <div className="relative flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] font-medium text-white/45">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] font-medium text-white/45">
                        Movement
                      </span>
                      {summary.bestLog ? (
                        <span className="rounded-full border border-[color:var(--training-accent-border)] bg-[color:var(--training-accent-soft)] px-2.5 py-1 text-[10px] font-medium text-[color:var(--training-accent-text)]">
                          Best logged
                        </span>
                      ) : null}
                    </div>

                    <h2 className="mt-3 text-xl font-semibold tracking-[-0.03em] text-white sm:text-[1.6rem]">
                      {movement.name}
                    </h2>

                    <p className="mt-2 max-w-2xl text-sm leading-6 text-white/62">
                      {movement.description}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {movement.metricTypes.map((metric) => (
                        <span
                          key={metric}
                          className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-white/68 transition duration-300 group-hover:border-white/15 group-hover:bg-white/[0.04]"
                        >
                          {metric}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-3 lg:min-w-[330px] lg:grid-cols-[1fr_auto] lg:items-center">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-[16px] border border-white/10 bg-white/[0.03] px-3.5 py-3">
                        <div className="text-[11px] font-medium text-white/45">Latest</div>
                        <div className="mt-2 text-sm font-medium text-white">
                          {summary.latestLog
                            ? formatDisplayValue(
                                summary.latestLog.value,
                                summary.latestLog.unit,
                                selectedCategory.key,
                                movement.name
                              )
                            : "No log yet"}
                        </div>
                        <div className="mt-1 text-xs text-white/48">
                          {summary.latestLog
                            ? `${summary.latestLog.metricType} · ${prettyDate(summary.latestLog.date)}`
                            : "Start tracking this movement"}
                        </div>
                      </div>

                      <div className="rounded-[16px] border border-white/10 bg-white/[0.03] px-3.5 py-3">
                        <div className="text-[11px] font-medium text-white/45">Best</div>
                        <div className="mt-2 text-sm font-medium text-white">
                          {summary.bestLog
                            ? formatDisplayValue(
                                summary.bestLog.value,
                                summary.bestLog.unit,
                                selectedCategory.key,
                                movement.name
                              )
                            : "No benchmark yet"}
                        </div>
                        <div className="mt-1 text-xs text-white/48">
                          {summary.bestLog
                            ? `${summary.totalLogs} ${summary.totalLogs === 1 ? "entry" : "entries"}`
                            : "Log twice to build a stronger record"}
                        </div>
                      </div>
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-medium text-white/55 transition duration-300 group-hover:border-white/18 group-hover:bg-white/[0.06] group-hover:text-white">
                      Open
                      <ChevronRight className="h-4 w-4 transition duration-300 group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </section>

        {loading ? (
          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5 text-sm text-white/55">
            Loading category progress...
          </div>
        ) : null}
      </div>
    </div>
  );
}
